import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../components/AuthProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Search, Download, Eye, FileText, ShieldAlert, FileSearch, MessageSquare, Lock, CheckCircle2, Loader2, AlertTriangle, Inbox, ExternalLink } from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { TemplatePreviewModal } from '../components/TemplatePreviewModal';
import { HRHubFooter } from '../components/HRHubFooter';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalShell = ({ title, icon: Icon, children, testid }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50" data-testid={testid}>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/members')} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />Back to Member Dashboard
          </button>
          <span className="font-semibold text-gray-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-blue-600" />}{title}
          </span>
          <span />
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
      <HRHubFooter />
    </div>
  );
};

const UpgradeWall = ({ requiredTier, currentTier, feature }) => {
  // "any" means "no active membership required tier" — use natural copy.
  const isAnyPlan = requiredTier === 'any';
  const headline = isAnyPlan ? 'Membership required' : `${requiredTier} feature`;
  const bodyText = isAnyPlan
    ? `${feature} requires an active membership.`
    : `${feature} requires the ${requiredTier} plan.`;
  const currentLine = currentTier
    ? `You're on ${currentTier[0].toUpperCase()}${currentTier.slice(1)} right now.`
    : "You don't have an active membership yet.";
  return (
    <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-200 text-center" data-testid="upgrade-wall">
      <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900">{headline}</h2>
      <p className="text-gray-600 mt-2 max-w-md mx-auto">
        {bodyText} {currentLine}
      </p>
      <Link to="/#memberships" className="inline-block mt-6 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="upgrade-wall-view-plans">
        View plans
      </Link>
    </div>
  );
};

// ============================ TEMPLATE LIBRARY ============================
export const TemplateLibraryPage = () => {
  useDocumentTitle('Template Library — HR Hub | DriveHR');
  const { user, authHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [activeCat, setActiveCat] = useState(searchParams.get('category') || '');
  const [dlState, setDlState] = useState({}); // templateId -> 'loading' | 'done' | 'error'
  const [previewing, setPreviewing] = useState(null); // currently-previewed template, or null

  // Keep activeCat + q in sync with the URL (so deep-links and back/forward work)
  useEffect(() => {
    const fromCat = searchParams.get('category') || '';
    const fromQ = searchParams.get('q') || '';
    setActiveCat((prev) => (prev === fromCat ? prev : fromCat));
    setQ((prev) => (prev === fromQ ? prev : fromQ));
  }, [searchParams]);

  const chooseCategory = (catId) => {
    setActiveCat(catId);
    const next = new URLSearchParams(searchParams);
    if (catId) next.set('category', catId); else next.delete('category');
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set('q', q.trim()); else next.delete('q');
    setSearchParams(next, { replace: true });
    load();
  };

  useEffect(() => {
    (async () => {
      try {
        const cats = await axios.get(`${API}/templates/categories`, { headers: authHeader() });
        setCategories(cats.data.categories);
      } catch (e) { /* ignore */ }
    })();
  }, [authHeader]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (activeCat) params.set('category', activeCat);
      const { data } = await axios.get(`${API}/templates?${params}`, { headers: authHeader() });
      setTemplates(data.templates);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCat, searchParams]);

  const handleDownload = async (t) => {
    setDlState((s) => ({ ...s, [t.id]: 'loading' }));
    try {
      const resp = await axios.get(`${API}/templates/${t.id}/download`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: t.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.file_name || t.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDlState((s) => ({ ...s, [t.id]: 'done' }));
      // Backend signals a re-download (already-downloaded template this month)
      // via an X-Drivehr-Redownload response header. Re-downloads don't
      // decrement the monthly counter, so show a brief explanatory toast.
      const isRedownload = String(resp.headers?.['x-drivehr-redownload'] || '').toLowerCase() === 'true';
      if (isRedownload) {
        toast.success("Re-downloaded — doesn't count against your monthly limit.");
      }
    } catch (e) {
      // With responseType:'blob', the error body comes back as a Blob — parse
      // it back to JSON so the 429 / 403 detail surfaces in the toast.
      let detail = null;
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          detail = JSON.parse(text).detail;
        } catch (_) { /* not JSON */ }
      } else {
        detail = data?.detail;
      }
      const msg = formatApiErrorDetail(detail) || e.message;
      setDlState((s) => ({ ...s, [t.id]: 'error', [`${t.id}__msg`]: msg }));
      toast.error(msg);
    }
  };

  if (!user) return null;
  if (user.membership_status !== 'active') {
    return <PortalShell title="Template Library" icon={FileText} testid="library-page"><UpgradeWall requiredTier="any" currentTier={user.membership_tier} feature="Access to the HR template library" /></PortalShell>;
  }

  return (
    <PortalShell title="Template Library" icon={FileText} testid="library-page">
      {/* Search + Filter */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 mb-6">
        <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates..." className="pl-10" data-testid="library-search" />
          </div>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Search</Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => chooseCategory('')} className={`px-3 py-1.5 rounded-full text-sm border transition ${activeCat === '' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid="cat-all">All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => chooseCategory(c.id)} className={`px-3 py-1.5 rounded-full text-sm border transition ${activeCat === c.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`cat-${c.id}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Templates grid */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin" /></div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-200 text-center text-gray-500" data-testid="library-empty">
          <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>No templates match those filters yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="library-grid">
          {templates.map((t) => {
            const dl = dlState[t.id];
            return (
              <div key={t.id} className="bg-white rounded-xl p-5 border border-gray-200 hover:border-blue-400 transition shadow-sm" data-testid={`template-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{t.name}</h3>
                    {t.description && <p className="text-sm text-gray-600 mt-1">{t.description}</p>}
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{categories.find((c) => c.id === t.category)?.label || t.category}</span>
                      <span className="text-gray-400">{(t.file_size / 1024).toFixed(0)} KB</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewing(t)}
                      data-testid={`preview-${t.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      <span className="text-xs">Preview</span>
                    </Button>
                    <Button size="sm" disabled={dl === 'loading'} onClick={() => handleDownload(t)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`download-${t.id}`}>
                      {dl === 'loading' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      <span className="text-xs">Download</span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {previewing && (
        <TemplatePreviewModal
          template={previewing}
          onClose={() => setPreviewing(null)}
          onDownload={(t) => { setPreviewing(null); handleDownload(t); }}
        />
      )}
    </PortalShell>
  );
};

// ============================ COMPLIANCE ALERTS ============================
export const ComplianceAlertsPage = () => {
  useDocumentTitle('Compliance Alerts — HR Hub | DriveHR');
  const { user, authHeader } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portal/compliance-alerts`, { headers: authHeader() });
        setAlerts(data.alerts);
        // Mark all current alerts as seen — clears the dashboard unread badge.
        try { await axios.post(`${API}/portal/compliance-alerts/mark-seen`, {}, { headers: authHeader() }); } catch (_) { /* badge stays — not critical */ }
      } catch (e) {
        if (e.response?.status === 403) setDenied(true);
      } finally { setLoading(false); }
    })();
  }, [authHeader]);

  if (denied) return <PortalShell title="Compliance Alerts" icon={ShieldAlert} testid="compliance-page"><UpgradeWall requiredTier="Pro or Elite" currentTier={user?.membership_tier} feature="Compliance Alerts" /></PortalShell>;

  const severityClasses = (s) => s === 'critical'
    ? 'border-red-200 bg-red-50 hover:border-red-300'
    : s === 'warn'
      ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
      : 'border-blue-200 bg-blue-50 hover:border-blue-300';

  return (
    <PortalShell title="Compliance Alerts" icon={ShieldAlert} testid="compliance-page">
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mt-10" /> :
        alerts.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-200 text-center text-gray-500">
            <ShieldAlert className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No active alerts right now — we'll post new federal and state updates here as they happen.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => {
              const inner = (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {a.title}
                      {a.link && <ExternalLink className="w-3.5 h-3.5 text-gray-400" />}
                    </h3>
                    <span className="text-xs text-gray-500">{new Date(a.published_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{a.body}</p>
                  {a.link && (
                    <p className="text-xs text-blue-600 mt-2 inline-flex items-center gap-1">
                      Open source <ExternalLink className="w-3 h-3" />
                    </p>
                  )}
                </>
              );
              const baseClass = `rounded-xl border p-5 shadow-sm transition ${severityClasses(a.severity)}`;
              return a.link ? (
                <a
                  key={a.id}
                  href={a.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${baseClass} block cursor-pointer hover:shadow-md`}
                  data-testid={`alert-card-${a.id}`}
                >{inner}</a>
              ) : (
                <div key={a.id} className={baseClass} data-testid={`alert-card-${a.id}`}>{inner}</div>
              );
            })}
          </div>
        )}
    </PortalShell>
  );
};

// ============================ ASK DRIVEHR ============================
export const AskDriveHRPage = () => {
  useDocumentTitle('Ask DriveHR — HR Hub | DriveHR');
  const { user, authHeader } = useAuth();
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const tier = user?.membership_tier;
  const allowed = user?.membership_status === 'active' && (tier === 'pro' || tier === 'elite');
  const limit = tier === 'elite' ? 5 : tier === 'pro' ? 3 : 0;

  if (!allowed) return <PortalShell title="Ask DriveHR" icon={MessageSquare} testid="ask-page"><UpgradeWall requiredTier="Pro or Elite" currentTier={tier} feature="Ask DriveHR email support" /></PortalShell>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setFlash(null);
    try {
      const { data } = await axios.post(`${API}/portal/ask`, { subject, question }, { headers: authHeader() });
      setFlash({ type: 'success', msg: `Sent — you've used ${data.used} of ${data.limit} this month.` });
      setSubject(''); setQuestion('');
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  return (
    <PortalShell title="Ask DriveHR" icon={MessageSquare} testid="ask-page">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <p className="text-sm text-gray-600 mb-2">
          Email-based HR guidance from real HR pros. {limit} requests/month included with {tier === 'elite' ? 'Elite' : 'Pro'}{tier === 'elite' ? ' · priority response' : ' · standard response'}.
        </p>
        <p className="text-sm text-gray-500 mb-4" data-testid="ask-response-sla">
          We'll reply to your registered email within <span className="font-semibold text-gray-700">1–2 business days</span>.
        </p>
        {flash && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="ask-flash">
            {flash.msg}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div><Label htmlFor="ask-subject">Subject</Label><Input id="ask-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="ask-subject" /></div>
          <div>
            <Label htmlFor="ask-question">Your question</Label>
            <Textarea id="ask-question" rows={6} value={question} onChange={(e) => setQuestion(e.target.value)} required data-testid="ask-question" />
            {/(https?:\/\/|www\.)\S+/i.test(question) && (
              <p className="text-xs text-gray-500 mt-1.5" data-testid="ask-link-helper">
                <span className="font-semibold text-gray-700">Tip:</span> If you&apos;ve pasted a Google Drive or Dropbox link, set it to &ldquo;Anyone with the link can view&rdquo; so we can access it. Your document stays in your own storage — we only view it.
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-xs text-gray-500" data-testid="ask-registered-email">
              Reply will be sent to <span className="font-semibold text-gray-700">{user?.email}</span>
            </p>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ask-submit">{busy ? 'Submitting...' : 'Send to DriveHR'}</Button>
          </div>
          <p className="text-xs text-gray-400 italic pt-1" data-testid="ask-legal-note">
            Responses are general HR guidance, not legal advice. For legal questions specific to your business, please consult a licensed attorney.
          </p>
        </form>
      </div>
    </PortalShell>
  );
};

// ============================ DOCUMENT REVIEW ============================
export const DocumentReviewPage = () => {
  useDocumentTitle('Document Review — HR Hub | DriveHR');
  const { user, authHeader } = useAuth();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [fileLink, setFileLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const allowed = user?.membership_status === 'active' && user?.membership_tier === 'elite';

  if (!allowed) return <PortalShell title="Document Review" icon={FileSearch} testid="doc-review-page"><UpgradeWall requiredTier="Elite" currentTier={user?.membership_tier} feature="A Document Review request" /></PortalShell>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setFlash(null);
    try {
      await axios.post(`${API}/portal/document-review`, { title, notes, file_link: fileLink }, { headers: authHeader() });
      setFlash({ type: 'success', msg: 'Submitted — we\'ll be in touch within 2 business days.' });
      setTitle(''); setNotes(''); setFileLink('');
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  return (
    <PortalShell title="Document Review" icon={FileSearch} testid="doc-review-page">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <p className="text-sm text-gray-600 mb-2">
          Submit a document for expert HR review. 1 review included per month with Elite. Share the document via a viewable link (Google Drive, Dropbox, OneDrive, etc.).
        </p>
        <p className="text-sm text-gray-500 mb-4" data-testid="dr-response-sla">
          We'll reply to your registered email within <span className="font-semibold text-gray-700">1–2 business days</span>.
        </p>
        {flash && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="doc-review-flash">
            {flash.msg}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div><Label htmlFor="dr-title">Document title</Label><Input id="dr-title" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="dr-title" /></div>
          <div>
            <Label htmlFor="dr-link">Document link (viewable)</Label>
            <Input id="dr-link" type="url" value={fileLink} onChange={(e) => setFileLink(e.target.value)} placeholder="https://drive.google.com/..." required data-testid="dr-link" />
            <p className="text-xs text-gray-500 mt-1.5" data-testid="dr-link-helper">
              <span className="font-semibold text-gray-700">Tip:</span> In Google Drive or Dropbox, set the link to &ldquo;Anyone with the link can view&rdquo; so we can access it. Your document stays in your own storage — we only view it.
            </p>
          </div>
          <div><Label htmlFor="dr-notes">What would you like us to review?</Label><Textarea id="dr-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dr-notes" /></div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-xs text-gray-500" data-testid="dr-registered-email">
              Reply will be sent to <span className="font-semibold text-gray-700">{user?.email}</span>
            </p>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dr-submit">{busy ? 'Submitting...' : 'Submit for review'}</Button>
          </div>
          <p className="text-xs text-gray-400 italic pt-1" data-testid="dr-legal-note">
            Responses are general HR guidance, not legal advice. For legal questions specific to your business, please consult a licensed attorney.
          </p>
        </form>
      </div>
    </PortalShell>
  );
};
